import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { PlusIcon as FiPlus } from '@heroicons/react/24/outline';
import {
  SIMPLIFIED_SECTIONS,
  STANDARDIZED_SECTIONS,
  itemKey,
} from './codesRubriquesData';

const NS = 'modules.salaires.pages.codesRubriquesPage';

const CodesRubriquesPage = () => {
  const { t } = useTranslation();
  const p = useCallback((key, opts) => t(`${NS}.${key}`, opts), [t]);
  const [activeSystem, setActiveSystem] = useState('simplifie');

  const renderCodeSection = (section, isStandardized = false) => {
    const titlePrefix = isStandardized ? section.prefix : section.range;

    return (
      <div className="bg-slate-800/50 backdrop-blur-sm p-4 rounded-xl border border-white/10 shadow-lg">
        <h3 className={`text-lg font-medium mb-3 ${section.colorClass}`}>
          {titlePrefix} : {p(section.sectionKey)}
        </h3>
        <div className="bg-slate-700/30 p-3 rounded-lg">
          <ul className="text-sm space-y-1 text-gray-300">
            {section.items.map((code) => (
              <li key={code}>
                <span className="font-mono bg-slate-700/50 px-2 py-0.5 rounded">{code}</span>
                {' - '}
                {p(itemKey(code))}
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-white">{p('title')}</h1>
        <p className="text-gray-300 mt-2">{p('subtitle')}</p>
      </div>

      <div className="flex space-x-4 mb-8">
        <button
          type="button"
          onClick={() => setActiveSystem('simplifie')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            activeSystem === 'simplifie'
              ? 'bg-blue-600 text-white'
              : 'bg-white/5 text-gray-300 hover:bg-white/10'
          }`}
        >
          {p('tab_simplified')}
        </button>

        <button
          type="button"
          onClick={() => setActiveSystem('standardise')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            activeSystem === 'standardise'
              ? 'bg-red-600 text-white'
              : 'bg-white/5 text-gray-300 hover:bg-white/10'
          }`}
        >
          {p('tab_standardized')}
        </button>
      </div>

      {activeSystem === 'simplifie' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            {SIMPLIFIED_SECTIONS.slice(0, Math.ceil(SIMPLIFIED_SECTIONS.length / 2)).map((section) =>
              renderCodeSection(section, false)
            )}
          </div>
          <div className="space-y-4">
            {SIMPLIFIED_SECTIONS.slice(Math.ceil(SIMPLIFIED_SECTIONS.length / 2)).map((section) =>
              renderCodeSection(section, false)
            )}
          </div>
        </div>
      )}

      {activeSystem === 'standardise' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            {STANDARDIZED_SECTIONS.slice(0, Math.ceil(STANDARDIZED_SECTIONS.length / 2)).map((section) =>
              renderCodeSection(section, true)
            )}
          </div>
          <div className="space-y-4">
            {STANDARDIZED_SECTIONS.slice(Math.ceil(STANDARDIZED_SECTIONS.length / 2)).map((section) =>
              renderCodeSection(section, true)
            )}
          </div>
        </div>
      )}

      <div className="bg-slate-800/50 backdrop-blur-sm p-4 rounded-xl border border-white/10 shadow-lg mt-8">
        <h3 className="text-lg font-medium mb-3 text-teal-300">{p('add_new_code')}</h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">{p('label_coding_system')}</label>
            <select className="w-full bg-slate-700/50 border border-slate-600 rounded-md px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="simple">{p('option_simplified')}</option>
              <option value="standard">{p('option_standardized')}</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">{p('label_code')}</label>
            <input
              type="text"
              placeholder={p('placeholder_code')}
              className="w-full bg-slate-700/50 border border-slate-600 rounded-md px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div className="md:col-span-3">
            <label className="block text-sm font-medium text-gray-300 mb-1">{p('label_label')}</label>
            <input
              type="text"
              placeholder={p('placeholder_label')}
              className="w-full bg-slate-700/50 border border-slate-600 rounded-md px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">{p('label_category')}</label>
            <select className="w-full bg-slate-700/50 border border-slate-600 rounded-md px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="base">{p('cat_base')}</option>
              <option value="prime_cotisable">{p('cat_prime_cotisable')}</option>
              <option value="indem_imposable">{p('cat_indem_imposable')}</option>
              <option value="indem_non_imposable">{p('cat_indem_non_imposable')}</option>
              <option value="frais">{p('cat_frais')}</option>
              <option value="retenue">{p('cat_retenue')}</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">{p('label_cotisable')}</label>
            <select className="w-full bg-slate-700/50 border border-slate-600 rounded-md px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="oui">{p('option_yes')}</option>
              <option value="non">{p('option_no')}</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">{p('label_imposable')}</label>
            <select className="w-full bg-slate-700/50 border border-slate-600 rounded-md px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="oui">{p('option_yes')}</option>
              <option value="non">{p('option_no')}</option>
            </select>
          </div>
        </div>

        <div className="flex justify-end">
          <button
            type="button"
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md flex items-center gap-2 transition-colors"
          >
            <FiPlus className="text-lg" />
            {p('add_code_btn')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CodesRubriquesPage;
